import { confirmToast } from "../../utils/toast";
import React, { useEffect, useMemo, useState } from "react";
import axios from "../../utils/axiosConfig";
import { normalizeCompanyName, publishCompanyName } from "../../utils/company";
import {
  collectValidationErrors,
  showValidationAlert,
  validateEmail,
  validatePAN,
  validateRequired,
  validateWebsite
} from "../../utils/fieldValidation";

const CompanyMaster = () => {
  const initialForm = {
    CompanyName: "GLOBAL METALS",
    Address: "",
    City: "",
    State: "",
    Country: "",
    Contact1: "",
    Contact2: "",
    SalesTaxNo: "",
    ServiceTaxNo: "",
    RegNo: "",
    IECCode: "",
    BankDetail: "",
    BankName: "",
    Branch: "",
    SwiftCode: "",
    BankAddress: "",
    IFSCCode: "",
    PANNo: "",
    TDSNo: "",
    Email: "",
    Website: "",
    Extra1: "",
    Extra2: "",
    Extra3: ""
  };

  const [form, setForm] = useState(initialForm);
  const [companyId, setCompanyId] = useState(null);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);

  const normalizeText = (value) => String(value || "").trim().toLowerCase();

  const selectedCountry = useMemo(() => {
    return countries.find((country) =>
      normalizeText(country.CountryName) === normalizeText(form.Country)
    );
  }, [countries, form.Country]);

  const selectedCountryId = selectedCountry?.CountryId || "";
  const hasCurrentCountryOption = countries.some((country) => country.CountryName === form.Country);
  const hasCurrentStateOption = states.some((state) => state.StateName === form.State);

  const normalizeLoadedCompany = (company) => {
    return Object.keys(initialForm).reduce((acc, key) => {
      acc[key] = company?.[key] ?? initialForm[key];
      return acc;
    }, {});
  };

  useEffect(() => {
    loadCompany();
    loadCountries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedCountryId) {
      loadStatesByCountry(selectedCountryId);
    } else {
      setStates([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountryId]);

  const loadCompany = async () => {
    try {
      const res = await axios.get("/company");

      if (res.data && Object.keys(res.data).length > 0) {
        setForm(normalizeLoadedCompany(res.data));
        setCompanyId(res.data.CompanyID || null);
        publishCompanyName(res.data.CompanyName);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const loadCountries = async () => {
    setLoadingCountries(true);
    try {
      const res = await axios.get("/countries");
      setCountries(res.data);
    } catch (err) {
      console.error("Load Countries Error:", err);
    } finally {
      setLoadingCountries(false);
    }
  };

  const loadStatesByCountry = async (countryId) => {
    setLoadingStates(true);
    try {
      const res = await axios.get(`/states/by-country/${countryId}`);
      setStates(res.data);
    } catch (err) {
      console.error("Load States Error:", err);
      setStates([]);
    } finally {
      setLoadingStates(false);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleCountryChange = (e) => {
    setForm({ ...form, Country: e.target.value, State: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = collectValidationErrors([
      validateRequired(form.CompanyName, "Company Name"),
      validateEmail(form.Email, "Company email"),
      validateWebsite(form.Website, "Company website"),
      validatePAN(form.PANNo, "PAN No")
    ]);

    if (showValidationAlert(errors)) return;

    try {
      const payload = { ...form, CompanyName: normalizeCompanyName(form.CompanyName) };
      if (companyId) {
        await axios.put(`/company/${companyId}`, payload);
      } else {
        await axios.post("/company/add", payload);
      }
      publishCompanyName(form.CompanyName);
      alert(companyId ? "Company updated successfully" : "Company saved successfully");
      loadCompany();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Unable to save company");
    }
  };

  const handleDelete = async () => {
    if (!companyId || !await confirmToast("Delete this company and its bank details?")) return;
    try {
      await axios.delete(`/company/${companyId}`);
      setForm({ ...initialForm });
      setCompanyId(null);
      publishCompanyName("");
      alert("Company deleted successfully");
    } catch (err) {
      alert(err.response?.data?.message || "Unable to delete company");
    }
  };

  return (
    <div className="container-fluid">
      <h4 className="mb-3 fw-bold">Company Settings</h4>

      <div className="card shadow-sm p-4">
        <form onSubmit={handleSubmit}>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="fw-semibold">Company Name *</label>
              <input
                name="CompanyName"
                className="form-control"
                value={form.CompanyName}
                onChange={handleChange}
                required
              />
            </div>

            <div className="col-md-3">
              <label>Country</label>
              <select
                name="Country"
                className="form-select"
                value={form.Country}
                onChange={handleCountryChange}
                disabled={loadingCountries}
              >
                <option value="">-- Select Country --</option>
                {form.Country && !hasCurrentCountryOption && (
                  <option value={form.Country}>{form.Country}</option>
                )}
                {countries.map((country) => (
                  <option key={country.CountryId} value={country.CountryName}>
                    {country.CountryName}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label>State</label>
              <select
                name="State"
                className="form-select"
                value={form.State}
                onChange={handleChange}
                disabled={loadingStates || (!selectedCountryId && !form.State)}
              >
                <option value="">-- Select State --</option>
                {form.State && !hasCurrentStateOption && (
                  <option value={form.State}>{form.State}</option>
                )}
                {states.map((state) => (
                  <option key={state.StateId} value={state.StateName}>
                    {state.StateName}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label>City</label>
              <input
                name="City"
                className="form-control"
                value={form.City}
                onChange={handleChange}
                placeholder="Enter city"
              />
            </div>

            <div className="col-md-3">
              <label>Contact 1</label>
              <input
                name="Contact1"
                className="form-control"
                value={form.Contact1}
                onChange={handleChange}
              />
            </div>

            <div className="col-md-3">
              <label>Contact 2</label>
              <input
                name="Contact2"
                className="form-control"
                value={form.Contact2}
                onChange={handleChange}
              />
            </div>

            <div className="col-md-3">
              <label>Email</label>
              <input
                name="Email"
                type="email"
                className="form-control"
                value={form.Email}
                onChange={handleChange}
              />
            </div>

            <div className="col-md-3">
              <label>Website</label>
              <input
                name="Website"
                className="form-control"
                value={form.Website}
                onChange={handleChange}
              />
            </div>

            <div className="col-12">
              <label>Address</label>
              <textarea
                name="Address"
                className="form-control"
                rows="3"
                value={form.Address}
                onChange={handleChange}
              />
            </div>

            <div className="col-md-3">
              <label>PAN No</label>
              <input
                name="PANNo"
                className="form-control"
                value={form.PANNo}
                onChange={handleChange}
              />
            </div>

            <div className="col-md-3">
              <label>GST / Sales Tax</label>
              <input
                name="SalesTaxNo"
                className="form-control"
                value={form.SalesTaxNo}
                onChange={handleChange}
              />
            </div>

            <div className="col-md-3">
              <label>Service Tax</label>
              <input
                name="ServiceTaxNo"
                className="form-control"
                value={form.ServiceTaxNo}
                onChange={handleChange}
              />
            </div>

            <div className="col-md-3">
              <label>Bank Detail</label>
              <input
                name="BankDetail"
                className="form-control"
                value={form.BankDetail}
                onChange={handleChange}
              />
            </div>
            <div className="col-12"><h5 className="mt-3">Bank Details</h5></div>
            {[
              ["BankName", "Bank Name", 255],
              ["Branch", "Branch", 255],
              ["SwiftCode", "Swift Code", 50],
              ["IFSCCode", "IFSC Code", 50]
            ].map(([name, label, maxLength]) => (
              <div className="col-md-6" key={name}>
                <label htmlFor={`company-${name}`}>{label}</label>
                <input id={`company-${name}`} type="text" name={name}
                  className="form-control" value={form[name]} maxLength={maxLength}
                  onChange={handleChange} />
              </div>
            ))}
            <div className="col-12">
              <label htmlFor="company-BankAddress">Bank Address</label>
              <textarea id="company-BankAddress" name="BankAddress" className="form-control"
                rows="3" value={form.BankAddress} onChange={handleChange} />
            </div>
          </div>

          <div className="mt-4 d-flex gap-2">
            <button className="btn btn-success">
              Save Settings
            </button>
            {companyId && (
              <button type="button" className="btn btn-outline-danger" onClick={handleDelete}>
                Delete Company
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompanyMaster;
